import { createContext, ReactNode, useContext, useMemo } from 'react';
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  AuthenticationApi,
  Configuration,
  IToken,
  WebAdminMedicalCentersApi,
  WebAdminStatisticsApi,
  WebAdminUsersApi,
  WebCompaniesApi,
  WebDoctorsApi,
  WebPatientsApi,
  WebPDFConfigurationsApi,
  WebReportsApi,
} from 'generated/axios';
import { TOKENS, useAuth } from './useAuth';
import { getCookie } from 'react-use-cookie';
import { useAuthApi } from './useAuthApi';

export interface BaseApiParams {
  configuration?: Configuration;
  basePath: undefined;
  axiosInstance: AxiosInstance;
}

interface ApiContextType {
  conf: BaseApiParams;
  userApi: WebAdminUsersApi;
  medicalCenterApi: WebAdminMedicalCentersApi;
  doctorsApi: WebDoctorsApi;
  patientApi: WebPatientsApi;
  companyApi: WebCompaniesApi;
  reportApi: WebReportsApi;
  pdfConfigApi: WebPDFConfigurationsApi;
  statisticsApi: WebAdminStatisticsApi;
}

const ApiContext = createContext<ApiContextType>({} as ApiContextType);

const axiosInstance: AxiosInstance = axios.create();

export function ApiContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const { access_token, updateTokens, removeTokens } = useAuth();

  const { authApi } = useAuthApi();

  //INTERCEPTOR RESPONSE OK
  const handleResponse = (res: AxiosResponse) => {
    return res;
  };

  //INTERCEPTOR RESPONSE KO
  const handleError = async (error: any) => {
    const originalConfig = error.config;
    // Access Token was expired
    if (error.response) {
      if (error.response.status === 401) {
        if (originalConfig._retry) {
          removeTokens();
        } else {
          originalConfig._retry = true;
          try {
            const resp = await authApi.refreshAccessToken({
              refresh_token: JSON.parse(getCookie(TOKENS)).refresh_token,
            });
            if (resp && resp.status === 200) {
              updateTokens(resp.data as IToken);
            } else {
              removeTokens();
              return Promise.reject(error);
            }
            return axiosInstance(originalConfig);
          } catch (_error) {
            return Promise.reject(_error);
          }
        }
      } else {
        return Promise.reject(error);
      }
    }
  };

  //INTERCEPTOR REQUEST OK
  const handleRequest = (conf: InternalAxiosRequestConfig) => {
    let temp = conf.headers;
    const rawTokens = getCookie(TOKENS);
    if (rawTokens) {
      const token = JSON.parse(rawTokens).access_token;
      if (token && temp) {
        temp['Authorization'] = 'Bearer ' + token;
      }
      conf.headers = temp;
    }

    /* conf.url = conf.url?.replaceAll('pageable.', ''); */
    return conf;
  };

  axiosInstance.interceptors.response.use(handleResponse, handleError);
  axiosInstance.interceptors.request.use(handleRequest);

  const cachedValue: ApiContextType = useMemo(
    () => ({
      conf: {
        configuration: new Configuration({ accessToken: access_token }),
        basePath: undefined,
        axiosInstance: axiosInstance,
      },
      userApi: new WebAdminUsersApi(new Configuration({ accessToken: access_token }), undefined, axiosInstance),
      medicalCenterApi: new WebAdminMedicalCentersApi(
        new Configuration({ accessToken: access_token }),
        undefined,
        axiosInstance,
      ),
      doctorsApi: new WebDoctorsApi(new Configuration({ accessToken: access_token }), undefined, axiosInstance),
      patientApi: new WebPatientsApi(new Configuration({ accessToken: access_token }), undefined, axiosInstance),
      companyApi: new WebCompaniesApi(new Configuration({ accessToken: access_token }), undefined, axiosInstance),
      reportApi: new WebReportsApi(new Configuration({ accessToken: access_token }), undefined, axiosInstance),
      pdfConfigApi: new WebPDFConfigurationsApi(
        new Configuration({ accessToken: access_token }),
        undefined,
        axiosInstance,
      ),
      statisticsApi: new WebAdminStatisticsApi(
        new Configuration({ accessToken: access_token }),
        undefined,
        axiosInstance,
      ),
      authApi: new AuthenticationApi(new Configuration({ accessToken: access_token }), undefined, axiosInstance),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [access_token],
  );

  return <ApiContext.Provider value={cachedValue}>{children}</ApiContext.Provider>;
}

//hook per utilizzo webSocket
export const useApi = () => useContext(ApiContext);
